import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Breadcrumb, Input, Spin, Tree } from "antd";
import type { DataNode } from "antd/es/tree";
import { SearchOutlined } from "@ant-design/icons";
import axios from "../../services/axios";
import { fetchAllPaginated } from "../../services/fetchPaginated";
import DefaultViewHeader from "../../components/main/default_view_header/DefaultViewHeader";
import HomeItem from "../../components/home/home_item/HomeItem";
import CreateDeviceModal from "../../components/home/create_device_modal/CreateDeviceModal";
import AuthContext from "../../contexts/AuthContext";
import { Home as HomeType } from "../../lib/Home";
import { Device } from "../../lib/Device";
import { homeMatchesSearchQuery } from "../../utils/search/homeSearch";
import "./installerPartners.scss";

type SubordinateInstaller = {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  customer_count: number;
  home_count: number;
  device_count: number;
  children: SubordinateInstaller[];
};

const displayName = (node: SubordinateInstaller) => {
  const name = `${node.first_name || ""} ${node.last_name || ""}`.trim();
  return name || node.username || node.email;
};

const flattenInstallers = (
  nodes: SubordinateInstaller[],
  map: Map<number, SubordinateInstaller> = new Map()
) => {
  nodes.forEach((node) => {
    map.set(node.id, node);
    if (node.children?.length) {
      flattenInstallers(node.children, map);
    }
  });
  return map;
};

const InstallerPartners = () => {
  const { t } = useTranslation("home");
  const { user } = useContext(AuthContext);

  const toTreeData = (nodes: SubordinateInstaller[]): DataNode[] =>
    nodes.map((node) => ({
      key: String(node.id),
      title: (
        <div className="partner-tree-node">
          <span className="partner-tree-node__name">{displayName(node)}</span>
          <span className="partner-tree-node__meta">
            {node.customer_count} {t("customers_label")} · {node.home_count}{" "}
            {t("homes_label")} · {node.device_count} {t("devices_label")}
          </span>
        </div>
      ),
      children: node.children?.length ? toTreeData(node.children) : undefined,
    }));
  const [tree, setTree] = useState<SubordinateInstaller[]>([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [selectedInstallerId, setSelectedInstallerId] = useState<number | null>(
    null
  );
  const [homes, setHomes] = useState<HomeType[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loadingFleet, setLoadingFleet] = useState(false);
  const [homeSearchQuery, setHomeSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [createDevice, setCreateDevice] = useState(false);
  const selectedHome = useRef(-1);

  const installerById = useMemo(() => flattenInstallers(tree), [tree]);
  const selectedInstaller = selectedInstallerId
    ? installerById.get(selectedInstallerId) ?? null
    : null;

  useEffect(() => {
    let cancelled = false;
    setLoadingTree(true);
    setError(null);
    axios
      .get("/api-v1/users/my-subordinate-installers/")
      .then((response) => {
        if (!cancelled) {
          setTree(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("partners_load_error"));
          setTree([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTree(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const loadFleet = useCallback(
    async (installerId: number) => {
      setLoadingFleet(true);
      setError(null);
      try {
        const [homesData, devicesData] = await Promise.all([
          fetchAllPaginated<HomeType>(
            `/api-v1/home/?installer_id=${installerId}&page_size=100`
          ),
          fetchAllPaginated<Device>(
            `/api-v1/device/?installer_id=${installerId}&page_size=100`
          ),
        ]);
        setHomes(homesData);
        setDevices(devicesData);
      } catch {
        setError(t("partners_fleet_load_error"));
        setHomes([]);
        setDevices([]);
      } finally {
        setLoadingFleet(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (selectedInstallerId == null) {
      setHomes([]);
      setDevices([]);
      return;
    }
    loadFleet(selectedInstallerId);
  }, [selectedInstallerId, loadFleet]);

  const filteredHomes = useMemo(() => {
    if (!homes?.length) return [];
    return homes.filter((h) =>
      homeMatchesSearchQuery(h, devices ?? null, homeSearchQuery)
    );
  }, [homes, devices, homeSearchQuery]);

  const groupedHomes = useMemo(() => {
    return filteredHomes.reduce((acc: Record<string, any>, home: HomeType) => {
      const customerKey = `${home.user_email}-${home.user_first_name}-${home.user_last_name}`;
      if (!acc[customerKey]) {
        acc[customerKey] = {
          customer: {
            email: home.user_email,
            first_name: home.user_first_name,
            last_name: home.user_last_name,
          },
          homes: [] as HomeType[],
        };
      }
      acc[customerKey].homes.push(home);
      return acc;
    }, {});
  }, [filteredHomes]);

  return (
    <div id="installer-partners-view">
      <CreateDeviceModal
        open={createDevice}
        device={selectedHome.current}
        close={() => {
          setCreateDevice(false);
          if (selectedInstallerId != null) {
            loadFleet(selectedInstallerId);
          }
        }}
      />
      <DefaultViewHeader
        title={t("partners_title")}
        description={t("partners_description")}
        big_title={true}
      />

      {error && <div className="partners-error">{error}</div>}

      <div className="partners-layout">
        <aside className="partners-tree-panel">
          <h3>{t("partners_tree_title")}</h3>
          {loadingTree ? (
            <div className="partners-loading">
              <Spin />
            </div>
          ) : tree.length === 0 ? (
            <p className="partners-empty">{t("partners_empty_tree")}</p>
          ) : (
            <Tree
              blockNode
              showLine={{ showLeafIcon: false }}
              treeData={toTreeData(tree)}
              defaultExpandAll
              selectedKeys={
                selectedInstallerId != null ? [String(selectedInstallerId)] : []
              }
              onSelect={(keys) => {
                const key = keys[0];
                setSelectedInstallerId(key != null ? Number(key) : null);
                setHomeSearchQuery("");
              }}
            />
          )}
        </aside>

        <section className="partners-fleet-panel">
          {!selectedInstaller ? (
            <p className="partners-empty">{t("partners_select_prompt")}</p>
          ) : (
            <>
              <Breadcrumb
                items={[
                  { title: t("partners_breadcrumb_root") },
                  { title: displayName(selectedInstaller) },
                ]}
              />
              <div className="installer-dashboard-strip">
                <div className="installer-dashboard-strip__stats">
                  <div>
                    <strong style={{ fontSize: "18px", color: "#1976d2" }}>
                      {(() => {
                        const uniqueCustomers = new Set(
                          homes.map(
                            (home) =>
                              `${home.user_email}-${home.user_first_name}-${home.user_last_name}`
                          )
                        );
                        return uniqueCustomers.size;
                      })()}
                    </strong>
                    <span style={{ marginLeft: "8px", color: "#666" }}>
                      {t("customers_label")}
                    </span>
                  </div>
                  <div>
                    <strong style={{ fontSize: "18px", color: "#1976d2" }}>
                      {homes.length}
                    </strong>
                    <span style={{ marginLeft: "8px", color: "#666" }}>
                      {t("homes_label")}
                    </span>
                  </div>
                  <div>
                    <strong style={{ fontSize: "18px", color: "#1976d2" }}>
                      {devices.length}
                    </strong>
                    <span style={{ marginLeft: "8px", color: "#666" }}>
                      {t("devices_label")}
                    </span>
                  </div>
                </div>
                <div className="installer-dashboard-strip__search">
                  <Input
                    allowClear
                    value={homeSearchQuery}
                    onChange={(e) => setHomeSearchQuery(e.target.value)}
                    placeholder={t("search_homes_placeholder")}
                    className="home-search-input"
                    prefix={<SearchOutlined className="home-search-prefix-icon" />}
                  />
                </div>
              </div>

              {loadingFleet ? (
                <div className="partners-loading">
                  <Spin />
                </div>
              ) : homes.length === 0 ? (
                <p className="partners-empty">{t("partners_empty_fleet")}</p>
              ) : filteredHomes.length === 0 && homeSearchQuery.trim() !== "" ? (
                <div className="home-search-empty">{t("search_homes_no_results")}</div>
              ) : (
                <div className="homes-container">
                  {Object.values(groupedHomes).map((group: any, groupIndex: number) => (
                    <div key={groupIndex} style={{ marginBottom: "24px" }}>
                      {user.show_customer_identity && (
                        <div
                          style={{
                            padding: "12px 16px",
                            backgroundColor: "#e3f2fd",
                            borderRadius: "8px",
                            marginBottom: "12px",
                            border: "1px solid #bbdefb",
                          }}
                        >
                          <h3
                            style={{
                              margin: 0,
                              fontSize: "16px",
                              fontWeight: 600,
                              color: "#1976d2",
                            }}
                          >
                            {group.customer.first_name} {group.customer.last_name}
                          </h3>
                          <p
                            style={{
                              margin: "4px 0 0 0",
                              fontSize: "14px",
                              color: "#666",
                            }}
                          >
                            {group.customer.email}
                          </p>
                        </div>
                      )}
                      {group.homes.map((home: HomeType, homeIndex: number) => (
                        <div key={homeIndex} style={{ marginBottom: "12px" }}>
                          <HomeItem
                            home={home}
                            devices={devices.filter((d) => d.home === home.id)}
                            new_device={(home_id: number) => {
                              selectedHome.current = home_id;
                              setCreateDevice(true);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default InstallerPartners;
